import { defineNuxtPlugin } from 'nuxt/app';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, initializeFirestore, type Firestore } from 'firebase/firestore';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import type { Messaging } from 'firebase/messaging';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

function createFirebaseFallback() {
    return {
        firebaseApp: null as FirebaseApp | null,
        db: null as Firestore | null,
        auth: {
            currentUser: null,
            app: null,
        } as unknown as Auth,
        storage: null as FirebaseStorage | null,
        messaging: null as Messaging | null,
    };
}

function hasFirebaseClientConfig(firebaseConfig: Record<string, unknown>) {
    return Object.values(firebaseConfig).every((value) => String(value ?? '').trim().length > 0);
}

export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig();

    const firebaseConfig = {
        apiKey: config.public.firebaseApiKey,
        authDomain: config.public.firebaseAuthDomain,
        projectId: config.public.firebaseProjectId,
        storageBucket: config.public.firebaseStorageBucket,
        messagingSenderId: config.public.firebaseMessagingSenderId,
        appId: config.public.firebaseAppId,
    };

    if (!hasFirebaseClientConfig(firebaseConfig)) {
        console.warn('[firebase] Missing client configuration. Firebase services are disabled for this session.');
        return {
            provide: createFirebaseFallback(),
        };
    }

    try {
        const firebaseApp = initializeApp(firebaseConfig);
        const appCheckSiteKey = String(config.public.firebaseAppCheckSiteKey || '').trim();
        if (appCheckSiteKey) {
            try {
                initializeAppCheck(firebaseApp, {
                    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
                    isTokenAutoRefreshEnabled: true,
                });
            } catch (appCheckError) {
                console.error('[firebase] Failed to initialize App Check.', appCheckError);
            }
        }
        // Optional local Firestore endpoint; Auth and Storage keep their normal config.
        const firestoreEmulatorUrl = String(config.public.firestoreEmulatorUrl || '').trim();
        const endpoint = firestoreEmulatorUrl ? new URL(firestoreEmulatorUrl) : null;
        if (endpoint && !['http:', 'https:'].includes(endpoint.protocol)) {
            throw new Error('Firestore emulator URL must use HTTP or HTTPS.');
        }
        const db = endpoint
            ? initializeFirestore(firebaseApp, { host: endpoint.host, ssl: endpoint.protocol === 'https:' })
            : getFirestore(firebaseApp);
        const auth = getAuth(firebaseApp);
        const authEmulatorUrl = String(config.public.firebaseAuthEmulatorUrl || '').trim();
        if (authEmulatorUrl) {
            const authEndpoint = new URL(authEmulatorUrl);
            if (!['http:', 'https:'].includes(authEndpoint.protocol)) {
                throw new Error('Firebase Auth emulator URL must use HTTP or HTTPS.');
            }
            connectAuthEmulator(auth, authEndpoint.origin, { disableWarnings: true });
        }

        const storage = getStorage(firebaseApp);
        const storageEmulatorUrl = String(config.public.firebaseStorageEmulatorUrl || '').trim();
        if (storageEmulatorUrl) {
            const storageEndpoint = new URL(storageEmulatorUrl);
            if (!['http:', 'https:'].includes(storageEndpoint.protocol)) {
                throw new Error('Firebase Storage emulator URL must use HTTP or HTTPS.');
            }
            const storagePort = Number(storageEndpoint.port || (storageEndpoint.protocol === 'https:' ? '443' : '80'));
            connectStorageEmulator(storage, storageEndpoint.hostname, storagePort);
        }

        return {
            provide: {
                firebaseApp,
                db,
                auth,
                storage,
                messaging: null,
            },
        };
    } catch (error) {
        console.error('[firebase] Failed to initialize client services.', error);

        return {
            provide: createFirebaseFallback(),
        };
    }
});
